import { useState, useEffect } from "react";
import { CheckCircle2, Play, FileText, Video, PenLine, ExternalLink } from "lucide-react";

// ── Mini wheat SVG ────────────────────────────────────────────────────────────
const MiniWheat = ({ style }) => (
  <svg viewBox="0 0 30 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
    <path d="M15 56 Q14 40 15 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <ellipse cx="15" cy="7"  rx="4" ry="6.5" fill="currentColor" opacity="0.9" transform="rotate(-10 15 7)"/>
    <ellipse cx="10" cy="18" rx="3.5" ry="6" fill="currentColor" opacity="0.7" transform="rotate(-28 10 18)"/>
    <ellipse cx="20" cy="18" rx="3.5" ry="6" fill="currentColor" opacity="0.7" transform="rotate(28 20 18)"/>
    <ellipse cx="9"  cy="29" rx="3" ry="5"   fill="currentColor" opacity="0.5" transform="rotate(-22 9 29)"/>
    <ellipse cx="21" cy="29" rx="3" ry="5"   fill="currentColor" opacity="0.5" transform="rotate(22 21 29)"/>
  </svg>
);

// ── Main ──────────────────────────────────────────────────────────────────────
export default function DetailsView({ scheme, onBack, language }) {
  const [activeTab, setActiveTab] = useState("documents");
  const [, setMounted]     = useState(false);
  const [govLinkWarning, setGovLinkWarning] = useState(null); 
  const isMarathi = language === "mr";

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 20);
    return () => clearTimeout(t);
  }, []);

  const TABS = [
    { key: "documents", label: isMarathi ? "कागदपत्रे"  : "Documents",   icon: FileText },
    { key: "video",     label: isMarathi ? "मार्गदर्शन" : "Video Guide", icon: Video    },
    { key: "apply",     label: isMarathi ? "अर्ज करा"   : "Apply",       icon: PenLine  },
  ];

  const APPLY_STEPS = [
    {
      step: "1",
      title:       isMarathi ? "अधिकृत वेबसाईटला भेट द्या" : "Visit Official Website",
      description: isMarathi ? "खालील बटणावर क्लिक करून अधिकृत पोर्टलवर जा." : "Click the button below to go to the official portal.",
    },
    {
      step: "2",
      title:       isMarathi ? "नोंदणी करा" : "Register",
      description: isMarathi ? "तुमचा आधार क्रमांक आणि मोबाइल नंबर वापरून नोंदणी करा." : "Register using your Aadhaar number and mobile number.",
    },
    {
      step: "3",
      title:       isMarathi ? "अर्ज भरा आणि सबमिट करा" : "Fill and Submit Application",
      description: isMarathi ? "सर्व आवश्यक माहिती भरा, कागदपत्रे अपलोड करा आणि अर्ज सबमिट करा." : "Fill all required details, upload documents, and submit.",
    },
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(170deg, #f0fdf4 0%, #f9fafb 55%, #f0fdf4 100%)",
      fontFamily: "'Noto Serif','Georgia',serif",
      paddingBottom: "clamp(80px, 15vh, 110px)",
      position: "relative",
      opacity: 1,
      transition: "opacity 0.35s ease",
    }}>

      {/* Ghost wheat corners */}
      {[
        { bottom: 55, left: -18,  width: 110, height: 170, opacity: 0.045, flip: false, delay: "0s",   dur: "4.5s" },
        { bottom: 50, left: 28,   width: 85,  height: 135, opacity: 0.030, flip: false, delay: "0.8s", dur: "5.5s" },
        { bottom: 55, right: -18, width: 110, height: 170, opacity: 0.045, flip: true,  delay: "0.4s", dur: "5s"   },
        { bottom: 50, right: 28,  width: 85,  height: 135, opacity: 0.030, flip: true,  delay: "1.2s", dur: "4.5s" },
      ].map((s, i) => (
        <div key={i} style={{
          position: "fixed", bottom: s.bottom,
          ...(s.flip ? { right: s.right } : { left: s.left }),
          width: s.width, height: s.height,
          color: "#1B4332", opacity: s.opacity,
          pointerEvents: "none", zIndex: 0,
          transformOrigin: "bottom center",
          animation: `${s.flip ? "wheatSwayFlip" : "wheatSway"} ${s.dur} ease-in-out ${s.delay} infinite`,
          willChange: "transform" 
        }}>
          <MiniWheat style={{ width: "100%", height: "100%" }} />
        </div>
      ))}

      {/* ── Content ── */}
      <div>

        {/* ── Header ── */}
        <div style={{
          background: "linear-gradient(135deg, #1B4332 0%, #2d6a4f 100%)",
          padding: "clamp(28px, 6vw, 36px) clamp(16px, 4vw, 24px) clamp(20px, 5vw, 24px)",
          position: "relative", overflow: "hidden",
        }}>
          {/* dot grid */}
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.05,
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}/>
          {/* yellow top line */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: "linear-gradient(90deg, transparent, #FACC15 30%, #FDE68A 50%, #FACC15 70%, transparent)",
          }}/>

          {/* ── 3 wheat stalks on RIGHT only ── */}
          {[
            { right: -6,  height: 112, width: 46, opacity: 0.22, delay: "0s",   dur: "4.5s" },
            { right: 32,  height: 84,  width: 35, opacity: 0.15, delay: "0.7s", dur: "5.2s" },
            { right: 66,  height: 62,  width: 26, opacity: 0.09, delay: "1.3s", dur: "3.8s" },
          ].map((s, i) => (
            <div key={i} style={{
              position: "absolute", bottom: -4, right: s.right,
              width: s.width, height: s.height,
              color: "#FACC15", opacity: s.opacity,
              transformOrigin: "bottom center",
              animation: `wheatSwayFlip ${s.dur} ease-in-out ${s.delay} infinite`,
              pointerEvents: "none",
              willChange: "transform" 
            }}>
              <MiniWheat style={{ width: "100%", height: "100%" }} />
            </div>
          ))}

          <div style={{ position: "relative", zIndex: 1 }}>
            {/* Back button */}
            <button onClick={onBack} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "none",
              borderRadius: 10, padding: "clamp(6px, 2vw, 8px) clamp(10px, 3vw, 14px)", cursor: "pointer",
              color: "rgba(255,255,255,0.85)", fontWeight: 700, fontSize: "clamp(12px, 3.5vw, 13px)",
              fontFamily: "inherit", marginBottom: "clamp(12px, 4vw, 16px)",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
            >
              ← {isMarathi ? "मागे" : "Back"}
            </button>

            <h1 style={{
              fontSize: "clamp(20px, 6vw, 28px)", fontWeight: 900,
              color: "#fff", lineHeight: 1.25, margin: "0 0 16px",
            }}>
              {scheme?.name?.[language]}
            </h1>

            {/* Benefit badge */}
            <div style={{
              display: "inline-block",
              background: "linear-gradient(135deg, #FACC15, #F59E0B)",
              borderRadius: 12, padding: "clamp(8px, 2.5vw, 12px) clamp(16px, 5vw, 24px)",
              boxShadow: "0 4px 16px rgba(250,204,21,0.4)",
            }}>
              <div style={{ fontSize: "clamp(10px, 2.5vw, 12px)", fontWeight: 700, color: "#1a2e22", letterSpacing: "0.08em", marginBottom: 2 }}>
                {isMarathi ? "मुख्य लाभ" : "MAIN BENEFIT"}
              </div>
              <div style={{ fontSize: "clamp(20px, 6vw, 26px)", fontWeight: 900, color: "#1a2e22" }}>
                {scheme?.benefitAmount?.[language]}
              </div>
            </div>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div style={{ padding: "clamp(12px, 3.5vw, 16px) clamp(12px, 3.5vw, 16px) clamp(6px, 2vw, 8px)" }}>
          <div style={{
            display: "flex", gap: "clamp(4px, 1vw, 6px)",
            background: "#e8f0eb", borderRadius: 16, padding: 5,
          }}>
            {TABS.map(tab => {
              const active = activeTab === tab.key;
              const Icon   = tab.icon;
              return (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                  padding: "clamp(8px, 2.5vw, 12px) 4px", borderRadius: 12, border: "none",
                  fontWeight: 700, fontSize: "clamp(11px, 3vw, 13px)", cursor: "pointer", fontFamily: "inherit",
                  transition: "all 0.22s ease",
                  background: active ? "#1B4332" : "transparent",
                  color:       active ? "#FACC15" : "#6b8f7a",
                  boxShadow:   active ? "0 3px 10px rgba(27,67,50,0.3)" : "none",
                }}>
                  <Icon size={"clamp(12px, 3.5vw, 14px)"} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Tab content ── */}
        <div style={{ padding: "clamp(6px, 2vw, 10px) clamp(12px, 3.5vw, 16px)" }}>

          {/* Documents */}
          {activeTab === "documents" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: "clamp(14px, 4vw, 16px)", fontWeight: 800, color: "#1a2e22", margin: "0 0 4px" }}>
                {isMarathi ? "आवश्यक कागदपत्रे" : "Required Documents"}
              </p>
              {scheme?.checklist?.[language]?.map((item, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "flex-start", gap: 12,
                  background: "#fff", borderRadius: 16, padding: "clamp(12px, 3.5vw, 14px) clamp(14px, 4vw, 16px)",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                  animation: `detailPopIn 0.4s cubic-bezier(0.34,1.56,0.64,1) ${i*80}ms both`,
                  willChange: "transform, opacity" 
                }}>
                  <CheckCircle2 size={18} style={{ color: "#1B4332", marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: "clamp(13px, 3.5vw, 15px)", color: "#374151", lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          )}

          {/* Video */}
          {activeTab === "video" && (
            <a href={scheme?.youtubeLink || "#"} target="_blank" rel="noopener noreferrer"
              style={{
                display: "block", borderRadius: 20, overflow: "hidden",
                background: "#fff", boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                textDecoration: "none",
                animation: "detailPopIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
                willChange: "transform, opacity" 
              }}>
              <div style={{
                aspectRatio: "16/9", display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #0d2b1a, #1B4332)",
                flexDirection: "column", gap: 12,
              }}>
                <div style={{
                  width: "clamp(50px, 12vw, 64px)", height: "clamp(50px, 12vw, 64px)", borderRadius: "50%",
                  background: "linear-gradient(135deg, #FF8C00, #FACC15)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 8px 24px rgba(255,140,0,0.4)",
                }}>
                  <Play size={"clamp(22px, 6vw, 28px)"} color="#1a2e22" fill="#1a2e22" style={{ marginLeft: 3 }} />
                </div>
                <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "clamp(12px, 3.5vw, 14px)", fontWeight: 600 }}>
                  {isMarathi ? "व्हिडिओ पहा" : "Watch Video"}
                </span>
              </div>
              <div style={{ padding: "clamp(14px, 4vw, 16px) clamp(16px, 4.5vw, 18px)" }}>
                <p style={{ fontSize: "clamp(15px, 4vw, 18px)", fontWeight: 900, color: "#1B4332", margin: "0 0 6px" }}>
                  {isMarathi ? "योजना कशी लागू करावी" : "How to Apply for this Scheme"}
                </p>
                <p style={{ fontSize: "clamp(12px, 3.5vw, 14px)", color: "#6b7280", lineHeight: 1.6, margin: 0 }}>
                  {isMarathi
                    ? "या व्हिडिओमध्ये अर्ज प्रक्रिया स्टेप-बाय-स्टेप समजावून सांगितली आहे."
                    : "This video explains the application process step-by-step."}
                </p>
              </div>
            </a>
          )}

          {/* Apply steps */}
          {activeTab === "apply" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ fontSize: "clamp(14px, 4vw, 16px)", fontWeight: 800, color: "#1a2e22", margin: "0 0 4px" }}>
                {isMarathi ? "ऑनलाइन अर्ज करण्याची प्रक्रिया" : "Online Application Process"}
              </p>
              {APPLY_STEPS.map((item, i) => (
                <div key={item.step} style={{
                  display: "flex", alignItems: "flex-start", gap: 14,
                  background: "#fff", borderRadius: 16, padding: "clamp(14px, 4vw, 16px)",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                  animation: `detailPopIn 0.4s cubic-bezier(0.34,1.56,0.64,1) ${i*100}ms both`,
                  willChange: "transform, opacity" 
                }}>
                  <div style={{
                    width: "clamp(34px, 10vw, 42px)", height: "clamp(34px, 10vw, 42px)", borderRadius: "50%", flexShrink: 0,
                    background: "linear-gradient(135deg, #1B4332, #40916c)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "clamp(14px, 4vw, 18px)", fontWeight: 900, color: "#FACC15",
                    boxShadow: "0 4px 12px rgba(27,67,50,0.28)",
                  }}>
                    {isMarathi ? ["१","२","३"][i] : item.step}
                  </div>
                  <div>
                    <p style={{ fontSize: "clamp(13px, 3.5vw, 15px)", fontWeight: 800, color: "#1a2e22", margin: "0 0 4px" }}>{item.title}</p>
                    <p style={{ fontSize: "clamp(12px, 3.5vw, 14px)", color: "#6b7280", lineHeight: 1.6, margin: 0 }}>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      {/* 👇 CHANGES MADE HERE: Added env() logic to the bottom padding 👇 */}
      {/* ── Sticky apply CTA ── */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        paddingTop: "clamp(8px, 2vw, 10px)",
        paddingLeft: "clamp(12px, 3.5vw, 16px)",
        paddingRight: "clamp(12px, 3.5vw, 16px)",
        paddingBottom: "calc(clamp(16px, 5vw, 22px) + env(safe-area-inset-bottom, 24px))",
        background: "linear-gradient(to top, #f0fdf4 75%, transparent)",
        zIndex: 10,
      }}>
        <button 
          onClick={() => setGovLinkWarning(scheme?.officialLink || "#")}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            width: "100%", padding: "clamp(12px, 3.5vw, 16px)",
            background: "linear-gradient(135deg, #FF8C00, #e05500)",
            color: "#fff", fontWeight: 900, fontSize: "clamp(14px, 4vw, 16px)",
            borderRadius: 14, border: "none", cursor: "pointer", 
            fontFamily: "inherit", letterSpacing: "0.04em",
            boxShadow: "0 6px 20px rgba(255,140,0,0.38)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 14px 36px rgba(255,140,0,0.52)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,140,0,0.38)"; }}
        >
          {isMarathi ? "अधिकृत वेबसाईटवर अर्ज करा" : "Apply on Official Website"}
          <ExternalLink size={"clamp(16px, 4vw, 20px)"} />
        </button>
      </div>

      {/* ── Gov Link Interceptor Modal ── */}
      {govLinkWarning && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 99999,
          background: "rgba(0, 0, 0, 0.6)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "20px"
        }}>
          <div style={{
            background: "#fff", borderRadius: "16px", padding: "clamp(20px, 6vw, 32px)",
            maxWidth: "400px", width: "100%", textAlign: "center",
            boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
          }}>
            <div style={{ fontSize: "clamp(32px, 10vw, 48px)", marginBottom: "16px" }}>⚠️</div>
            <h3 style={{ margin: "0 0 12px", color: "#1a2e22", fontSize: "clamp(18px, 5vw, 24px)", fontWeight: "800" }}>
              {language === "mr" ? "कृपया लक्ष द्या" : "Heads Up!"}
            </h3>
            <p style={{ margin: "0 0 24px", color: "#6b8f7a", fontSize: "clamp(13px, 3.5vw, 16px)", lineHeight: "1.5" }}>
              {language === "mr" 
                ? "सरकारी वेबसाइट्स अनेकदा संथ असतात किंवा देखभालीसाठी बंद असतात. जर लिंक उघडली नाही, तर कृपया परत या आणि त्याऐवजी व्हिडिओ ट्यूटोरियल पहा!" 
                : "Government websites are often slow or down for maintenance. If the page fails to load, simply come back here and watch the video tutorial instead!"}
            </p>
            <div style={{ display: "flex", gap: "12px", flexDirection: "column" }}>
              <button 
                onClick={() => {
                  window.open(govLinkWarning, "_blank", "noopener,noreferrer");
                  setGovLinkWarning(null);
                }}
                style={{
                  padding: "clamp(12px, 3.5vw, 14px)", borderRadius: "8px", border: "none",
                  background: "#1B4332", color: "#fff", fontWeight: "bold", cursor: "pointer", fontFamily: "inherit"
                }}>
                {language === "mr" ? "पुढे जा" : "Proceed to Portal"}
              </button>
              <button 
                onClick={() => setGovLinkWarning(null)}
                style={{
                  padding: "clamp(12px, 3.5vw, 14px)", borderRadius: "8px", border: "1px solid #c8d8ce",
                  background: "transparent", color: "#6b8f7a", fontWeight: "bold", cursor: "pointer", fontFamily: "inherit"
                }}>
                {language === "mr" ? "रद्द करा" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes detailPopIn {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes wheatSway {
          0%,100% { transform: rotate(4deg)  translateX(0px);  }
          40%      { transform: rotate(8deg)  translateX(2px);  }
          70%      { transform: rotate(1deg)  translateX(-2px); }
        }
        @keyframes wheatSwayFlip {
          0%,100% { transform: scaleX(-1) rotate(4deg)  translateX(0px);  }
          40%      { transform: scaleX(-1) rotate(8deg)  translateX(2px);  }
          70%      { transform: scaleX(-1) rotate(1deg)  translateX(-2px); }
        }
      `}</style>
    </div>
  );
}